import { Button as MuiButton, type ButtonProps as MuiButtonProps } from '@mui/material';
import type { LucideIcon } from 'lucide-react';
import type { ReactElement } from 'react';
import { Link } from 'react-admin';

import { Icon } from './Icon';

/**
 * Bouton du back-office : quatre roles, deux tailles (maquette « BO/Bouton »).
 *
 * Il existe parce que Material UI raisonne en *variantes* (`contained`,
 * `outlined`, `text`) et en *couleurs*, la ou la maquette raisonne en **roles** :
 * nommer le role au site d'appel empeche de fabriquer un cinquieme bouton par
 * combinaison.
 *
 * - `primary` — l'action principale d'un ecran, **une seule** par ecran ;
 * - `secondary` — toute autre action ;
 * - `ghost` — action de faible poids (« Voir », « Journal ») ;
 * - `danger` — action irreversible. **Toujours derriere une confirmation** : ce
 *   bouton ouvre un dialogue, ou il est le bouton de validation de ce dialogue —
 *   jamais un geste direct. Son libelle nomme l'action reelle (« Anonymiser ce
 *   compte »), jamais « Supprimer » pour ce qui n'est pas detruit.
 *
 * Taille M : 40 px de haut ; taille S : 30 px, pour les lignes de tableau et
 * les barres d'outils denses. Le libelle est un texte, pas un noeud : un bouton
 * se nomme par ce qu'il fait. Pas de `sx` : l'apparence est celle du role, la
 * mise en page (marges, alignement) revient au conteneur.
 */
export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';

export interface ButtonProps
  extends Omit<MuiButtonProps, 'variant' | 'color' | 'size' | 'startIcon' | 'endIcon' | 'children' | 'sx'> {
  readonly variant?: ButtonVariant;
  readonly size?: 'M' | 'S';
  readonly icon?: LucideIcon;
  /**
   * Destination interne : le bouton devient un **lien** (`<a>`), avec le routeur
   * de l'application. Un bouton qui change d'ecran est un lien — le clic
   * central, « ouvrir dans un onglet » et le role lu par les lecteurs d'ecran en
   * dependent.
   */
  readonly to?: string;
  readonly children: string;
}

const MUI_VARIANT = {
  primary: { variant: 'contained', color: 'primary' },
  secondary: { variant: 'outlined', color: 'inherit' },
  ghost: { variant: 'text', color: 'inherit' },
  danger: { variant: 'contained', color: 'error' },
} as const satisfies Record<ButtonVariant, Pick<MuiButtonProps, 'variant' | 'color'>>;

/** Ce que les variantes MUI ne savent pas dire avec les jetons du mobile. */
const ROLE_SX = {
  primary: {},
  danger: {},
  secondary: {
    color: 'tokens.textStrong',
    bgcolor: 'tokens.surface',
    borderColor: 'tokens.border',
    '&:hover': { bgcolor: 'tokens.surfaceMuted', borderColor: 'tokens.textMuted' },
  },
  // `accentPinkStrong` et non `primary` : un bouton fantome peut se poser sur le
  // fond de page, ou `primary` clair n'atteint que 4,45:1 (tokens.contrast.test).
  ghost: {
    color: 'tokens.accentPinkStrong',
    '&:hover': { bgcolor: 'tokens.primarySoft' },
  },
} as const;

export const Button = ({
  variant = 'secondary',
  size = 'M',
  icon,
  to,
  children,
  ...rest
}: ButtonProps): ReactElement => (
  <MuiButton
    {...rest}
    {...(to === undefined ? {} : { component: Link, to })}
    {...MUI_VARIANT[variant]}
    size={size === 'S' ? 'small' : 'medium'}
    startIcon={icon === undefined ? undefined : <Icon icon={icon} size={size === 'S' ? 16 : 18} />}
    sx={ROLE_SX[variant]}
  >
    {children}
  </MuiButton>
);
